/**
 * Club Owner Settings Page
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Bell, Lock, CreditCard, HelpCircle,
  ChevronRight, LogOut, User, Shield, Radio, ShoppingCart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import ClubNavbar from '../../components/ClubNavbar';
import { useAuthStore } from '../../store';
import { toast } from 'sonner';

// Settings item component
const SettingsItem = ({ icon: Icon, title, description, onClick, variant = "default" }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
      variant === 'danger'
        ? 'hover:bg-red-500/10 text-red-500'
        : 'hover:bg-secondary/50'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        variant === 'danger' ? 'bg-red-500/10' : 'bg-primary/10'
      }`}>
        <Icon className={`w-5 h-5 ${variant === 'danger' ? 'text-red-500' : 'text-primary'}`} />
      </div>
      <div className="text-left">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
    <ChevronRight className={`w-5 h-5 ${variant === 'danger' ? 'text-red-500' : 'text-muted-foreground'}`} />
  </button>
);

export default function ClubSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  
  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/auth');
  };
  
  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      <div className="max-w-2xl mx-auto p-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-display font-bold text-2xl md:text-3xl mb-1">Configuración</h1>
          <p className="text-muted-foreground">Administra tu cuenta y preferencias</p>
        </motion.div>
        
        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="bg-gradient-to-r from-primary/10 to-blue-500/10 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg">
                    {user?.first_name} {user?.last_name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    Club Owner
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Account Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Cuenta</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                <SettingsItem
                  icon={User}
                  title="Perfil"
                  description="Editar información personal"
                  onClick={() => navigate('/club/profile')}
                />
                <SettingsItem
                  icon={Lock}
                  title="Seguridad"
                  description="Contraseña y autenticación"
                  onClick={() => toast.info('Próximamente')}
                />
                <SettingsItem
                  icon={Bell}
                  title="Notificaciones"
                  description="Configura alertas y avisos"
                  onClick={() => toast.info('Próximamente')}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Botones Shelly */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-6"
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Botones Shelly</CardTitle>
              <CardDescription>Gestiona tus botones de replay</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                <SettingsItem
                  icon={Radio}
                  title="Mis Botones"
                  description="Ver y configurar botones Shelly"
                  onClick={() => navigate('/club/buttons')}
                />
                <SettingsItem
                  icon={ShoppingCart}
                  title="Comprar Botones"
                  description="Precio club: $500 MXN c/u"
                  onClick={() => navigate('/club/buttons?action=buy')}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Business Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Negocio</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                <SettingsItem
                  icon={Building2}
                  title="Datos del Club"
                  description="Información y configuración del club"
                  onClick={() => navigate('/club/profile/edit')}
                />
                <SettingsItem
                  icon={CreditCard}
                  title="Facturación"
                  description="Historial de pagos y facturas"
                  onClick={() => toast.info('Próximamente')}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Soporte</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                <SettingsItem
                  icon={HelpCircle}
                  title="Centro de Ayuda"
                  description="Preguntas frecuentes y tutoriales"
                  onClick={() => toast.info('Próximamente')}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
